// Small facts about a call on the tablet (STAGE_5 5.3): what the one main button does,
// who the patient is in a line, and where "Navigator" and "Call" lead.

import { ageOf } from '@/features/calls/intake';
import { ru } from '@/i18n/ru';

import type { CrewCall } from './crewApi';
import type { CrewStatus } from './crewActions';

const k = ru.crew.calls;

/** The next move and its label (TZ 15.3: one main button, saying what comes next). */
export function nextMove(call: Pick<CrewCall, 'status' | 'type'>): { to: CrewStatus; label: string } | null {
  switch (call.status) {
    case 'assigned':
      return { to: 'en_route', label: k.next.assigned };
    case 'en_route':
      return { to: 'on_scene', label: k.next.en_route };
    case 'on_scene':
      return call.type === 'transport'
        ? { to: 'transporting', label: k.next.transport }
        : { to: 'completed', label: k.next.complete };
    case 'transporting':
      return { to: 'completed', label: k.next.complete };
    default:
      return null;
  }
}

/** Waiting counts on scene and on the way with the patient (TZ 5.2). */
export function canWait(call: Pick<CrewCall, 'status'>): boolean {
  return call.status === 'on_scene' || call.status === 'transporting';
}

/** A destination belongs to a transport call, from departure to arrival. */
export function canChangeDestination(call: Pick<CrewCall, 'status' | 'type'>): boolean {
  return (
    call.type === 'transport' &&
    (call.status === 'en_route' || call.status === 'on_scene' || call.status === 'transporting')
  );
}

/** "Karimov Aziz, 54 g, m" - the name in full on the tablet: the doctor meets the person. */
export function patientText(call: Pick<CrewCall, 'patient_name' | 'patient_birth_date' | 'patient_sex'>, now: number): string {
  const age = call.patient_birth_date ? ageOf(call.patient_birth_date, now) : null;
  return [
    call.patient_name,
    ...(age ? [ru.calls.ageShort(age.value, age.unit)] : []),
    ...(call.patient_sex ? [ru.calls.sexShort[call.patient_sex]] : []),
  ].join(', ');
}

/** The payer: the patient, or the organization of the contract. */
export function payerText(call: Pick<CrewCall, 'payer_type' | 'organization_name'>): string {
  return call.payer_type === 'organization' && call.organization_name
    ? call.organization_name
    : ru.calls.payerTypes[call.payer_type];
}

/** The number to call: the patient's, else whoever called. */
export function phoneOf(call: Pick<CrewCall, 'patient_phone' | 'caller_phone'>): string | null {
  return call.patient_phone ?? call.caller_phone;
}

/** Where the crew drives now: the scene, or the destination once the patient is aboard. */
export function driveTarget(call: CrewCall): { lat: number | null; lng: number | null; address: string } {
  if (call.status === 'transporting' && (call.dest_lat !== null || call.dest_address)) {
    return {
      lat: call.dest_lat,
      lng: call.dest_lng,
      address: call.dest_hospital_name ?? call.dest_address ?? call.address,
    };
  }
  return { lat: call.lat, lng: call.lng, address: call.address };
}

/** Google Maps navigation straight away; `geo:` for any other map app (TZ 8). Without
 * a point on the map, the address is searched. */
export function navigatorLinks(target: { lat: number | null; lng: number | null; address: string }): {
  primary: string;
  fallback: string;
} {
  if (target.lat !== null && target.lng !== null) {
    const point = `${String(target.lat)},${String(target.lng)}`;
    return { primary: `google.navigation:q=${point}`, fallback: `geo:${point}?q=${point}` };
  }
  const query = encodeURIComponent(target.address);
  return { primary: `google.navigation:q=${query}`, fallback: `geo:0,0?q=${query}` };
}

/** How long the fallback waits: if the navigator opened, the page is hidden by then. */
export const NAVIGATOR_FALLBACK_MS = 1500;

/** Open the navigator; fall back to `geo:` when nothing took the first link. */
export function openNavigator(
  target: { lat: number | null; lng: number | null; address: string },
  go: (url: string) => void = (url) => {
    window.location.href = url;
  },
): void {
  const { primary, fallback } = navigatorLinks(target);
  go(primary);
  window.setTimeout(() => {
    if (document.visibilityState === 'visible') go(fallback);
  }, NAVIGATOR_FALLBACK_MS);
}

/** The first assignment the crew has not accepted yet, if any. */
export function unaccepted(calls: readonly CrewCall[] | undefined): CrewCall | undefined {
  return calls?.find((call) => call.status === 'assigned' && call.accepted_at === null);
}

/** Completed, not paid in full, and something to pay: the medic's next thing to do. */
export function isOwed(call: Pick<CrewCall, 'status' | 'payment_status' | 'total_amount'>): boolean {
  return call.status === 'completed' && call.payment_status !== 'paid' && call.total_amount > 0;
}

/** The call card is the crew's to change until money is taken and while the call is
 * open (services.call_pricing.follow_card prices it again then); after, it is read. */
export function cardEditable(call: Pick<CrewCall, 'status' | 'payment_status'>): boolean {
  return call.payment_status === 'unpaid' && !['closed', 'cancelled'].includes(call.status);
}
